import { ActorRef, DesignPackageDetail, DesignPackageItem } from "@/services/bedrock/dtos/DesignPackage";
import {
  DesignPackageActivityAction,
  DesignPackageActivityEvent,
  RefinedPayload,
} from "@/services/bedrock/dtos/DesignPackageActivity";
import { isItemInProgress } from "./slideState";

export type RequestOutcome = "rendering" | "ready" | "kept" | "discarded" | "failed";

export type RequestScope = "all" | { itemId: number; position: number };

/** A coach's refinement, or an admin's edited code applied as a new version. */
export type RequestSource = "refine" | "html_edit";

export type ThreadEntry =
  | {
      kind: "request";
      id: number;
      actor: ActorRef;
      at: string;
      /** The refinement text, or the admin's note (may be empty for an html_edit). */
      instructions: string;
      source: RequestSource;
      scope: RequestScope;
      outcome: RequestOutcome;
      thumbnails: { itemId: number; templateId: number | null; url: string | null }[];
    }
  | { kind: "generated"; id: number; actor: ActorRef | null; at: string; instructions: string }
  | { kind: "system"; id: number; action: DesignPackageActivityAction; actor: ActorRef; at: string; text: string };

export const ACTIVITY_LABELS: Record<DesignPackageActivityAction, string> = {
  generated: "Created the design",
  refined: "Requested a change",
  revision_approved: "Kept a new version",
  revision_discarded: "Discarded a version",
  published: "Published",
  unpublished: "Unpublished",
  prompt_updated: "Updated who receives it",
  renamed: "Renamed",
  archived: "Archived",
  restored: "Restored",
  duplicated: "Duplicated",
  rendered_for_player: "Rendered for a player",
  background_default_changed: "Changed a default background",
  backgrounds_generated: "Added generated backgrounds",
  background_uploaded: "Uploaded a background",
  background_attached: "Added a background",
  background_removed: "Removed a background",
  background_policy_changed: "Changed what players can choose",
  custom_backgrounds_toggled: "Changed player photo setting",
  html_edited: "Updated the design",
};

export function asUser(name: string): string {
  return `as ${name}`;
}

const SYSTEM_ACTIONS: DesignPackageActivityAction[] = [
  "published",
  "unpublished",
  "prompt_updated",
  "renamed",
  "archived",
  "restored",
  "duplicated",
  "rendered_for_player",
];

// Posts made for players matter whichever slide is in view, so they show under every filter.
const ALWAYS_SHOWN: DesignPackageActivityAction[] = ["rendered_for_player"];

function systemText(event: DesignPackageActivityEvent): string {
  if (event.action === "renamed") return `${ACTIVITY_LABELS.renamed} to “${(event.payload as { to: string }).to}”`;
  if (event.action === "rendered_for_player") {
    const { playerName, reviewIds } = event.payload as { playerName: string; reviewIds: number[] };
    const reviews =
      reviewIds.length === 0 ? "" : ` using ${reviewIds.length} review${reviewIds.length === 1 ? "" : "s"}`;
    return `Created a post for ${playerName}${reviews}`;
  }
  return ACTIVITY_LABELS[event.action];
}

type RevisionPayload = { designPackageItemId: number; designTemplateId: number; previewUrl: string | null };

function byCreatedAt(a: DesignPackageActivityEvent, b: DesignPackageActivityEvent): number {
  const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  return diff !== 0 ? diff : a.id - b.id;
}

function templateOutcome(
  templateId: number,
  item: DesignPackageItem | undefined,
  later: DesignPackageActivityEvent[]
): RequestOutcome {
  const revision = later.find(
    e =>
      (e.action === "revision_approved" || e.action === "revision_discarded") &&
      (e.payload as RevisionPayload).designTemplateId === templateId
  );
  if (revision) return revision.action === "revision_approved" ? "kept" : "discarded";
  if (item?.candidateTemplate?.id === templateId) {
    if (isItemInProgress(item)) return "rendering";
    if (item.status === "failed") return "failed";
    return "ready";
  }
  // The revision event may be beyond the loaded pages: an active template was kept, anything
  // else no longer exists.
  if (item?.activeTemplate.id === templateId) return "kept";
  return "discarded";
}

function aggregate(outcomes: RequestOutcome[]): RequestOutcome {
  if (outcomes.length > 0 && outcomes.every(o => o === "kept")) return "kept";
  if (outcomes.length > 0 && outcomes.every(o => o === "discarded")) return "discarded";
  if (outcomes.includes("failed")) return "failed";
  if (outcomes.includes("rendering")) return "rendering";
  return "ready";
}

function thumbnailUrl(
  templateId: number | null,
  item: DesignPackageItem | undefined,
  later: DesignPackageActivityEvent[]
): string | null {
  if (templateId == null) return null;
  const revision = later.find(
    e =>
      (e.action === "revision_approved" || e.action === "revision_discarded") &&
      (e.payload as RevisionPayload).designTemplateId === templateId
  );
  if (revision) return (revision.payload as RevisionPayload).previewUrl;
  if (item?.candidateTemplate?.id === templateId) return item.candidateTemplate.previewUrl;
  if (item?.activeTemplate.id === templateId) return item.activeTemplate.previewUrl;
  return null;
}

function requestEntry(
  event: DesignPackageActivityEvent,
  later: DesignPackageActivityEvent[],
  pkg: DesignPackageDetail,
  source: RequestSource = "refine"
): ThreadEntry {
  const payload = event.payload as RefinedPayload;
  const itemIds = payload.designPackageItemIds ?? [];
  const templateIds = payload.designTemplateIds ?? [];
  const itemFor = (itemId: number) => pkg.items.find(i => i.id === itemId);

  const scope: RequestScope =
    payload.scope === "all" ? "all" : { itemId: itemIds[0], position: itemFor(itemIds[0])?.position ?? 0 };

  let outcome: RequestOutcome;
  if (templateIds.length === 0) {
    // The refine job hasn't reported its candidates yet.
    outcome = itemIds.some(id => itemFor(id)?.status === "failed") ? "failed" : "rendering";
  } else {
    outcome = aggregate(templateIds.map((templateId, i) => templateOutcome(templateId, itemFor(itemIds[i]), later)));
  }

  const thumbnails = itemIds.map((itemId, i) => {
    const templateId = templateIds[i] ?? null;
    return { itemId, templateId, url: thumbnailUrl(templateId, itemFor(itemId), later) };
  });

  return {
    kind: "request",
    id: event.id,
    actor: event.actor,
    at: event.createdAt,
    instructions: payload.instructions ?? "",
    source,
    scope,
    outcome,
    thumbnails,
  };
}

// Kept and discarded versions show as the outcome of their request, never as their own line.
const OUTCOME_ACTIONS: DesignPackageActivityAction[] = ["revision_approved", "revision_discarded"];

/**
 * The refinement thread, built from activity events only. With `everything`, every other event
 * (publishing, background changes, posts for players) joins it as a one-line entry.
 */
export function buildThread(
  events: DesignPackageActivityEvent[],
  pkg: DesignPackageDetail,
  { everything = false }: { everything?: boolean } = {}
): ThreadEntry[] {
  const sorted = [...events].sort(byCreatedAt);
  const entries: ThreadEntry[] = [];

  sorted.forEach((event, index) => {
    switch (event.action) {
      case "generated":
        entries.push({
          kind: "generated",
          id: event.id,
          actor: event.actor ?? null,
          at: event.createdAt,
          instructions: (event.payload as { instructions: string }).instructions,
        });
        return;
      case "refined":
        entries.push(requestEntry(event, sorted.slice(index + 1), pkg));
        return;
      case "html_edited":
        entries.push(requestEntry(event, sorted.slice(index + 1), pkg, "html_edit"));
        return;
      default:
        if (OUTCOME_ACTIONS.includes(event.action)) return;
        if (!everything && !SYSTEM_ACTIONS.includes(event.action)) return;
        entries.push({
          kind: "system",
          id: event.id,
          action: event.action,
          actor: event.actor,
          at: event.createdAt,
          text: systemText(event),
        });
    }
  });

  return entries;
}

export type ThreadFilter = "slide" | "all" | "everything";

/**
 * What the Activity panel shows: the selected slide's requests (and whole-design ones), every
 * request, or every event. Other events appear only under "everything", except posts made for
 * players, which show under every filter.
 */
export function filterThread(
  entries: ThreadEntry[],
  filter: ThreadFilter,
  selectedItemId: number | null
): ThreadEntry[] {
  if (filter === "everything") return entries;
  return entries.filter(entry => {
    if (entry.kind === "generated") return true;
    if (entry.kind === "system") return ALWAYS_SHOWN.includes(entry.action);
    if (filter === "all" || entry.scope === "all") return true;
    return entry.scope.itemId === selectedItemId;
  });
}
